import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Package } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [subscription, setSubscription] = useState(null);
  const [hardwareIncluded, setHardwareIncluded] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("failed");
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("failed");
      toast.error("Payment verification timed out. Please contact support.");
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, { withCredentials: true });
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        setSubscription(response.data.subscription);
        setHardwareIncluded(response.data.hardware_included || false);
        toast.success("Welcome to phygital!");
        return;
      } else if (response.data.status === "expired" || response.data.payment_status === "failed") {
        setStatus("failed");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      if (attempts >= 5) {
        setStatus("failed");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    }
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-12 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-black mx-auto mb-6" />
            <h1 className="font-display text-2xl font-bold mb-2">
              Confirming your payment...
            </h1>
            <p className="text-gray-600">
              Please wait while we activate your subscription.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-display text-2xl font-bold mb-2">
              Payment Failed
            </h1>
            <p className="text-gray-600 mb-8">
              We couldn't process your payment. Please try again or contact support.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate("/pricing")}
                className="bg-black hover:bg-gray-900 text-white rounded-full"
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/wallet")}
                className="rounded-full"
              >
                Go to Wallet
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <Card className="max-w-lg w-full">
        <CardContent className="p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          
          <h1 className="font-display text-3xl font-bold mb-2">
            Welcome to phygital!
          </h1>
          
          {subscription && (
            <Badge className="bg-black text-white mb-6">
              {subscription.plan?.charAt(0).toUpperCase() + subscription.plan?.slice(1)} Plan
            </Badge>
          )}
          
          <p className="text-gray-600 mb-8">
            Your subscription is now active. You have full access to all features.
          </p>

          {hardwareIncluded && (
            <div className="bg-gray-50 rounded-2xl p-6 mb-8 text-left">
              <div className="flex items-center gap-3 mb-3">
                <Package className="w-6 h-6 text-black" />
                <h3 className="font-display font-semibold">Hardware Shipping</h3>
              </div>
              <p className="text-sm text-gray-700">
                Your NFC hardware kit will be shipped within 3-5 business days. 
                You'll receive a tracking number via email.
              </p>
            </div>
          )}

          <div className="space-y-3">
            <Button
              onClick={() => navigate("/wallet")}
              className="w-full bg-black hover:bg-gray-900 text-white rounded-full py-6"
            >
              Go to Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <Button
              variant="outline"
              onClick={() => navigate("/subscription")}
              className="w-full rounded-full"
            >
              Manage Subscription
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
